import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

let isRefreshing = false;
let failedQueue = [];

const api = axios.create({
  baseURL: process.env.REACT_APP_URL_BASE,
});

// Função auxiliar para processar a fila de requisições
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Função para verificar se o token está expirado
const isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token);
    const now = Date.now() / 1000;
    return decoded.exp < now; // se exp < agora, já expirou
  } catch {
    return true; // token inválido ou não decodificável
  }
};

// --- INTERCEPTOR DE REQUEST --- //
api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('token');

  if (!token) return config;

  // Verifica se o token já expirou
  if (isTokenExpired(token)) {
    if (!isRefreshing) {
      isRefreshing = true;

      try {
        const refreshApi = axios.create({
          baseURL: api.defaults.baseURL,
          headers: {
            Authorization: `Bearer ${token}`, // Envia o token expirado para o endpoint de refresh
          },
        });
        const res = await refreshApi.post('/auth/refresh');
        const { token: newAccessToken } = res.data;

        localStorage.setItem('token', newAccessToken);
        api.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);

        config.headers.Authorization = `Bearer ${newAccessToken}`;
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('sessionExpired'));
        return new Promise(() => {}); // Evita erro no console
      } finally {
        isRefreshing = false;
      }
    } else {
      // Espera o refresh atual terminar
      const newToken = await new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      });
      config.headers.Authorization = `Bearer ${newToken}`;
    }
  } else {
    // Token válido
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Se for FormData, deixar o axios calcular o Content-Type automaticamente
  // NÃO sobrescrever o Content-Type para FormData
  if (config.data instanceof FormData) {
    // Remover o Content-Type padrão para FormData calcular com boundary correto
    delete config.headers['Content-Type'];
    console.log('📤 Enviando FormData - Content-Type será definido automaticamente pelo axios');
  }

  return config;
});

// --- INTERCEPTOR DE RESPONSE --- //
api.interceptors.response.use(
  response => response,
  async (error) => {
    // Caso algum erro inesperado aconteça (ex: refresh falhou)
    const originalRequest = error.config;

    // Só fazer logout se for 401 em uma rota que NÃO é de login/refresh
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Se é uma requisição de login ou refresh, deixar o erro passar sem fazer logout
      if (originalRequest.url.includes('/auth/login') || originalRequest.url.includes('/auth/refresh')) {
        return Promise.reject(error);
      }
      
      // Só dispara sessionExpired para outras rotas
      localStorage.removeItem('token');
      window.dispatchEvent(new Event('sessionExpired'));
      return new Promise(() => {}); // Silencia o erro no console
    }

    return Promise.reject(error);
  }
);

// --- MÉTODO CUSTOMIZADO PARA REQUISIÇÕES SEM /api --- //
api.getWithoutBaseAPI = async (url, config = {}) => {
  const baseUrl = process.env.REACT_APP_URL_BASE.replace(/\/api\/?$/, '');
  const fullUrl = `${baseUrl}${url}`;
  const token = localStorage.getItem('token');
  
  const headers = { ...config.headers };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  return axios.get(fullUrl, { ...config, headers });
};

export default api;